import { useCallback } from 'react';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, Info } from 'lucide-react';
import { useUpdateTask } from '@/hooks/useTasks';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface Task {
  id: string;
  sheetId: string;
  constraintType?: string;
  constraintDate?: string | null;
  deadline?: string | null;
  taskType?: string;
  effortDriven?: boolean;
  isCritical: boolean;
  earlyStart?: string | null;
  earlyFinish?: string | null;
  lateStart?: string | null;
  lateFinish?: string | null;
  totalSlack?: number | null;
  freeSlack?: number | null;
}

interface TaskSchedulingProps {
  task: Task;
  sheetId: string;
}

const CONSTRAINT_TYPES = [
  { value: 'ASAP', label: 'As Soon As Possible', description: 'Default, flexible' },
  { value: 'ALAP', label: 'As Late As Possible', description: 'Flexible, schedules late' },
  { value: 'MSO', label: 'Must Start On', description: 'Inflexible, requires date', requiresDate: true },
  { value: 'MFO', label: 'Must Finish On', description: 'Inflexible, requires date', requiresDate: true },
  { value: 'SNET', label: 'Start No Earlier Than', description: 'Semi-flexible', requiresDate: true },
  { value: 'SNLT', label: 'Start No Later Than', description: 'Semi-flexible', requiresDate: true },
  { value: 'FNET', label: 'Finish No Earlier Than', description: 'Semi-flexible', requiresDate: true },
  { value: 'FNLT', label: 'Finish No Later Than', description: 'Semi-flexible', requiresDate: true },
];

const TASK_TYPES = [
  { value: 'fixed_units', label: 'Fixed Units' },
  { value: 'fixed_work', label: 'Fixed Work' },
  { value: 'fixed_duration', label: 'Fixed Duration' },
];

export function TaskScheduling({ task, sheetId }: TaskSchedulingProps) {
  const updateTask = useUpdateTask(sheetId);

  const handleUpdate = useCallback(
    (field: string, value: unknown) => {
      updateTask.mutate({
        taskId: task.id,
        updates: { [field]: value },
      });
    },
    [task.id, updateTask]
  );

  const handleConstraintTypeChange = useCallback(
    (value: string) => {
      handleUpdate('constraintType', value);
      // Clear constraint date if switching to a type that doesn't require it
      const constraint = CONSTRAINT_TYPES.find((c) => c.value === value);
      if (!constraint?.requiresDate && task.constraintDate) {
        handleUpdate('constraintDate', null);
      }
    },
    [handleUpdate, task.constraintDate]
  );

  const handleConstraintDateChange = useCallback(
    (date: Date | undefined) => {
      handleUpdate('constraintDate', date ? date.toISOString().split('T')[0] : null);
    },
    [handleUpdate]
  );

  const handleDeadlineChange = useCallback(
    (date: Date | undefined) => {
      handleUpdate('deadline', date ? date.toISOString().split('T')[0] : null);
    },
    [handleUpdate]
  );

  const handleTaskTypeChange = useCallback(
    (value: string) => {
      handleUpdate('taskType', value);
    },
    [handleUpdate]
  );

  const handleEffortDrivenChange = useCallback(
    (checked: boolean) => {
      handleUpdate('effortDriven', checked);
    },
    [handleUpdate]
  );

  const currentConstraint = CONSTRAINT_TYPES.find(
    (c) => c.value === task.constraintType
  );
  const requiresConstraintDate = currentConstraint?.requiresDate ?? false;

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString();
  };

  const formatSlack = (slack: number | null | undefined) => {
    if (slack === null || slack === undefined) return '—';
    return `${slack} ${slack === 1 ? 'day' : 'days'}`;
  };

  return (
    <TooltipProvider>
      <div className="space-y-6 pt-4 border-t">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
          Scheduling
        </h3>

        {/* Constraint Type */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Label>Constraint Type</Label>
            <Tooltip>
              <TooltipTrigger>
                <Info className="h-4 w-4 text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p>Controls when this task can be scheduled relative to other tasks and dates.</p>
              </TooltipContent>
            </Tooltip>
          </div>
          <Select
            value={task.constraintType || 'ASAP'}
            onValueChange={handleConstraintTypeChange}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select constraint" />
            </SelectTrigger>
            <SelectContent>
              {CONSTRAINT_TYPES.map((constraint) => (
                <SelectItem key={constraint.value} value={constraint.value}>
                  <div className="flex flex-col">
                    <span>{constraint.label}</span>
                    <span className="text-xs text-muted-foreground">
                      {constraint.description}
                    </span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Constraint Date (shown only when required) */}
        {requiresConstraintDate && (
          <div className="space-y-2">
            <Label>Constraint Date</Label>
            <DatePicker
              value={task.constraintDate ? new Date(task.constraintDate) : null}
              onChange={handleConstraintDateChange}
              placeholder="Select constraint date"
            />
          </div>
        )}

        {/* Deadline */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Label>Deadline</Label>
            <Tooltip>
              <TooltipTrigger>
                <Info className="h-4 w-4 text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p>A soft target date. Unlike constraints, deadlines don't affect scheduling but will show warnings if missed.</p>
              </TooltipContent>
            </Tooltip>
          </div>
          <DatePicker
            value={task.deadline ? new Date(task.deadline) : null}
            onChange={handleDeadlineChange}
            placeholder="Set deadline"
          />
        </div>

        {/* Task Type and Effort Driven */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Label>Task Type</Label>
              <Tooltip>
                <TooltipTrigger>
                  <Info className="h-4 w-4 text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p>Determines which value stays fixed when you modify one of Duration, Work, or Units.</p>
                </TooltipContent>
              </Tooltip>
            </div>
            <Select
              value={task.taskType || 'fixed_units'}
              onValueChange={handleTaskTypeChange}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {TASK_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Label>Effort Driven</Label>
              <Tooltip>
                <TooltipTrigger>
                  <Info className="h-4 w-4 text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p>When enabled, adding resources reduces duration to maintain the same total work.</p>
                </TooltipContent>
              </Tooltip>
            </div>
            <div className="pt-2">
              <Switch
                checked={task.effortDriven ?? false}
                onCheckedChange={handleEffortDrivenChange}
              />
            </div>
          </div>
        </div>

        {/* Calculated Fields */}
        <div className="space-y-4 pt-4 border-t">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium">Calculated Values</h4>
            {task.isCritical && (
              <Badge variant="destructive" className="flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                Critical Path
              </Badge>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="space-y-1">
              <span className="text-muted-foreground">Early Start</span>
              <p className="font-medium">{formatDate(task.earlyStart)}</p>
            </div>
            <div className="space-y-1">
              <span className="text-muted-foreground">Early Finish</span>
              <p className="font-medium">{formatDate(task.earlyFinish)}</p>
            </div>
            <div className="space-y-1">
              <span className="text-muted-foreground">Late Start</span>
              <p className="font-medium">{formatDate(task.lateStart)}</p>
            </div>
            <div className="space-y-1">
              <span className="text-muted-foreground">Late Finish</span>
              <p className="font-medium">{formatDate(task.lateFinish)}</p>
            </div>
            <div className="space-y-1">
              <span className="text-muted-foreground">Total Slack</span>
              <p className="font-medium">{formatSlack(task.totalSlack)}</p>
            </div>
            <div className="space-y-1">
              <span className="text-muted-foreground">Free Slack</span>
              <p className="font-medium">{formatSlack(task.freeSlack)}</p>
            </div>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}

export default TaskScheduling;
